import {
  DownloadOutlined,
  ExportOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { Button, message } from 'antd';
import { ParseResult } from 'papaparse';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCSVReader, usePapaParse } from 'react-papaparse';

import { OpenClass } from '../../openClasses/types/OpenClass';
import { certificateTypeLabel } from '../../openClasses/utils/certificateTypeLabel';
import { downloadCsv } from '../../utils/csv';
import {
  AttendeeCsvRow,
  attendeeCsvExportColumns,
  draftsFromCsvRows,
  exportRowsFromDrafts,
} from '../utils/openClassAttendeeCsv';
import { ParticipantDraft } from '../utils/openClassParticipantDraft';

const exampleFileName = 'SafeLife-SampleAttendeesCSVFile.csv';

type OpenClassAttendeeCsvButtonsProps = {
  canCreate: boolean;
  drafts: ParticipantDraft[];
  openClasses: OpenClass[];
  onImported: (drafts: ParticipantDraft[], skippedCount: number) => void;
};

const OpenClassAttendeeCsvButtons: React.FC<
  OpenClassAttendeeCsvButtonsProps
> = ({ canCreate, drafts, openClasses, onImported }) => {
  const { t } = useTranslation();
  const { jsonToCSV } = usePapaParse();
  const { CSVReader } = useCSVReader();

  const onExport = () => {
    const csv = jsonToCSV(
      exportRowsFromDrafts(drafts, openClasses, certificateTypeLabel),
      { columns: attendeeCsvExportColumns },
    );
    downloadCsv(
      `${t(
        'views.OpenClassBookingOrderDetails.attendeesExportFileName',
      )}.csv`.replace(/\s+/g, '-'),
      csv,
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canCreate && (
        <CSVReader
          config={{ header: true, skipEmptyLines: true }}
          onUploadAccepted={({ data, errors }: ParseResult<AttendeeCsvRow>) => {
            if (errors.length > 0) {
              message.error(
                <>
                  <span>{t('errors.somethingWentWrong')}</span>
                  <div>{JSON.stringify(errors)}</div>
                </>,
              );
            }
            const { drafts: imported, skippedCount } = draftsFromCsvRows(data);
            onImported(imported, skippedCount);
          }}>
          {({
            getRootProps,
          }: {
            getRootProps: () => React.HTMLAttributes<HTMLElement>;
          }) => (
            <Button icon={<ImportOutlined />} {...getRootProps()}>
              {t('views.OpenClassBookingOrderDetails.importCsv')}
            </Button>
          )}
        </CSVReader>
      )}
      <Button icon={<ExportOutlined />} onClick={onExport}>
        {t('views.OpenClassBookingOrderDetails.exportCsv')}
      </Button>
      {canCreate && (
        <a
          download={exampleFileName}
          href={process.env.REACT_APP_PUBLIC_URL + exampleFileName}>
          <Button icon={<DownloadOutlined />}>
            {t('views.OpenClassBookingOrderDetails.downloadCsvExample')}
          </Button>
        </a>
      )}
    </div>
  );
};

export default OpenClassAttendeeCsvButtons;
